import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { EmployeeRecord } from '../types/employees';

interface EmployeeDBSchema extends DBSchema {
  employees: {
    key: string;
    value: EmployeeRecord;
  };
}

const dbCache = new Map<string, Promise<IDBPDatabase<EmployeeDBSchema>>>();

function getDB(dbKey: string) {
  let promise = dbCache.get(dbKey);
  if (!promise) {
    promise = openDB<EmployeeDBSchema>(dbKey, 1, {
      upgrade(db) {
        db.createObjectStore('employees', { keyPath: 'employeeUUID' });
      },
    });
    dbCache.set(dbKey, promise);
  }
  return promise;
}

export async function getAllEmployees(dbKey: string): Promise<EmployeeRecord[]> {
  const db = await getDB(dbKey);
  return db.getAll('employees');
}

export async function putAllEmployees(dbKey: string, records: EmployeeRecord[]): Promise<void> {
  const db = await getDB(dbKey);
  const tx = db.transaction('employees', 'readwrite');
  await Promise.all([...records.map((r) => tx.store.put(r)), tx.done]);
}

export async function putEmployee(dbKey: string, record: EmployeeRecord): Promise<void> {
  const db = await getDB(dbKey);
  await db.put('employees', record);
}

export async function getEmployee(dbKey: string, uuid: string): Promise<EmployeeRecord | undefined> {
  const db = await getDB(dbKey);
  return db.get('employees', uuid);
}

export function getEmployeeFetchTime(dbKey: string): number {
  const raw = localStorage.getItem(`fetchTime:${dbKey}`);
  return raw ? parseInt(raw, 10) : 0;
}

export function setEmployeeFetchTime(dbKey: string, timestamp: number): void {
  localStorage.setItem(`fetchTime:${dbKey}`, String(timestamp));
}

export function getFrontendCacheUUID(dbKey: string): string | null {
  return localStorage.getItem(`frontendCacheUUID:${dbKey}`);
}

export function setFrontendCacheUUID(dbKey: string, uuid: string): void {
  localStorage.setItem(`frontendCacheUUID:${dbKey}`, uuid);
}

export async function clearEmployeeDB(dbKey: string): Promise<void> {
  try {
    const db = await getDB(dbKey);
    const tx = db.transaction('employees', 'readwrite');
    await tx.store.clear();
    await tx.done;
  } catch {
    // If IDB is unavailable, drop the cached promise so the next getDB call
    // starts fresh. The sync will rebuild from an empty state.
    dbCache.delete(dbKey);
  }
  setEmployeeFetchTime(dbKey, 0);
}

export async function deleteEmployee(dbKey: string, uuid: string): Promise<void> {
  const db = await getDB(dbKey);
  await db.delete('employees', uuid);
}
